import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import {
  Heart,
  MessageCircle,
  Play,
  ChevronDown,
  Sparkles,
  Search,
  X,
  ArrowLeft,
  BookOpen,
  Wand2,
  Swords,
  Laugh,
  Flame,
  Ghost,
  HeartHandshake,
  Brain,
  Rocket,
  Crown,
  Palette,
  Zap
} from 'lucide-react';
import { toast } from 'sonner';

// Category configuration with emoji/icon, gradient, and description
const CATEGORY_DATA = [
  { id: 'Trending', label: 'Trending', emoji: '🔥', gradient: 'from-orange-500 to-red-600', description: 'Hottest comics right now', icon: Flame },
  { id: 'Action', label: 'Action', emoji: '⚔️', gradient: 'from-red-600 to-rose-700', description: 'Epic battles & adventures', icon: Swords },
  { id: 'Comedy', label: 'Comedy', emoji: '😂', gradient: 'from-yellow-500 to-amber-600', description: 'Laugh out loud stories', icon: Laugh },
  { id: 'Drama', label: 'Drama', emoji: '🎭', gradient: 'from-purple-600 to-indigo-700', description: 'Intense emotional tales', icon: Crown },
  { id: 'Romance', label: 'Romance', emoji: '💕', gradient: 'from-pink-500 to-rose-600', description: 'Love stories that captivate', icon: HeartHandshake },
  { id: 'Dark Fantasy', label: 'Dark Fantasy', emoji: '🌑', gradient: 'from-slate-700 to-purple-900', description: 'Dark mystical worlds', icon: Ghost },
  { id: 'Psych Thriller', label: 'Psych Thriller', emoji: '🧠', gradient: 'from-emerald-600 to-teal-800', description: 'Mind-bending suspense', icon: Brain },
  { id: 'Sci-Fi', label: 'Sci-Fi', emoji: '🚀', gradient: 'from-cyan-500 to-blue-700', description: 'Futuristic adventures', icon: Rocket },
  { id: 'AI Comics', label: 'AI Comics', emoji: '🤖', gradient: 'from-violet-500 to-fuchsia-600', description: 'AI-generated masterpieces', icon: Wand2 },
  { id: 'Horror', label: 'Horror', emoji: '👻', gradient: 'from-gray-800 to-red-950', description: 'Spine-chilling tales', icon: Ghost },
  { id: 'Slice of Life', label: 'Slice of Life', emoji: '☕', gradient: 'from-amber-500 to-orange-600', description: 'Everyday moments', icon: BookOpen },
  { id: 'Superhero', label: 'Superhero', emoji: '🦸', gradient: 'from-blue-600 to-indigo-700', description: 'Heroes rise up', icon: Zap },
  { id: 'Manga', label: 'Manga', emoji: '📖', gradient: 'from-rose-500 to-pink-700', description: 'Japanese-style comics', icon: BookOpen },
  { id: 'Webtoon', label: 'Webtoon', emoji: '📱', gradient: 'from-green-500 to-emerald-700', description: 'Scroll-based stories', icon: Palette },
];

const Home = () => {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [comics, setComics] = useState([]);
  const [loading, setLoading] = useState(true);
  const activeCategory = searchParams.get('category') || null;
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [selectedComic, setSelectedComic] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [categoryCounts, setCategoryCounts] = useState({});

  // Handle comic selection - opens modal
  const handleComicClick = (comic) => {
    setSelectedComic(comic);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  // Fetch comics for a specific category
  const fetchComics = useCallback(async (category, search) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: '100' });

      if (category && category !== 'All' && category !== 'Trending') {
        params.append('category', category);
      }
      if (search && search.trim()) {
        params.append('search', search.trim());
      }

      const response = await axios.get(`${API}/streamlined-comics?${params.toString()}`);
      setComics(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching comics:', error);
      toast.error('Failed to load comics');
      setLoading(false);
    }
  }, [API]);

  // Fetch all comics on mount for category counts + initial display
  useEffect(() => {
    fetchComics(activeCategory, searchQuery);
  }, [activeCategory, searchQuery, fetchComics]);

  // Fetch category counts on mount
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const response = await axios.get(`${API}/streamlined-comics?limit=500`);
        const allComics = response.data || [];
        const counts = {};
        allComics.forEach(comic => {
          (comic.categories || []).forEach(cat => {
            counts[cat] = (counts[cat] || 0) + 1;
          });
        });
        counts['Trending'] = allComics.length;
        setCategoryCounts(counts);
      } catch (err) {
        console.error('Error fetching category counts:', err);
      }
    };
    fetchCounts();
  }, [API]);

  // Auto-select first comic when comics load
  useEffect(() => {
    if (comics.length > 0 && !selectedComic) {
      setSelectedComic(comics[0]);
    }
  }, [comics]);

  // Handle category card click - pushes to browser history
  const handleCategoryClick = (categoryId) => {
    setSearchParams({ category: categoryId });
    setSelectedComic(null);
  };

  // Go back to categories view
  const handleBackToCategories = () => {
    setSearchParams({});
    setSelectedComic(null);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
    setIsSearching(searchInput.trim().length > 0);
    if (searchInput.trim()) {
      setSearchParams({ category: 'search' });
    }
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setIsSearching(false);
    if (activeCategory === 'search') {
      setSearchParams({});
    }
  };

  const handleLike = async (comicId, e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please login to like comics');
      return;
    }

    try {
      await axios.post(`${API}/comics/${comicId}/like`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      setComics(prev => prev.map(comic =>
        comic.id === comicId
          ? { ...comic, likes_count: (comic.likes_count || 0) + 1 }
          : comic
      ));

      toast.success('Comic liked!');
    } catch (error) {
      console.error('Error liking comic:', error);
      toast.error('Failed to like comic');
    }
  };

  // Current active category info
  const activeCategoryInfo = CATEGORY_DATA.find(c => c.id === activeCategory);

  return (
    <div className="min-h-screen relative overflow-hidden">
      <section className="relative min-h-screen overflow-hidden">

        {/* Fullscreen Background */}
        <div className="absolute inset-0 z-0">
          {activeCategory && selectedComic?.episodes?.[0]?.slides?.[0] ? (
            <img
              key={selectedComic.id}
              src={selectedComic.episodes[0].slides[0]}
              alt=""
              className="w-full h-full object-cover transition-all duration-1000 ease-out"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900"></div>
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-purple-950/80 to-slate-900/70"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-slate-950/50"></div>
          <div className="absolute top-0 right-[-10%] w-[600px] h-[600px] bg-purple-600/10 rounded-full blur-[150px] animate-pulse"></div>
          <div className="absolute bottom-[-20%] left-[-10%] w-[500px] h-[500px] bg-violet-500/10 rounded-full blur-[120px]"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 min-h-screen flex flex-col">
          {/* Top Bar */}
          <div className="px-6 lg:px-12 py-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
              <div className="flex items-center gap-3">
                {activeCategory && (
                  <button
                    onClick={handleBackToCategories}
                    className="p-2 rounded-xl bg-slate-800/60 border border-purple-500/20 text-gray-400 hover:text-white hover:border-purple-500/40 transition-all"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                )}
                <h1 className="text-2xl md:text-3xl font-black text-white uppercase tracking-tight">
                  Browse <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-violet-400">Comics</span>
                </h1>
              </div>

              {/* Search Bar */}
              <form onSubmit={handleSearchSubmit} className="relative group max-w-md flex-1 lg:flex-initial">
                <div className="relative flex items-center bg-slate-950/60 backdrop-blur-md border border-purple-500/20 rounded-xl p-1.5 transition-all duration-300 group-focus-within:border-purple-500/50 group-focus-within:shadow-[0_0_20px_rgba(168,85,247,0.2)]">
                  <Search className="absolute left-3 w-4 h-4 text-gray-500 group-focus-within:text-purple-400 transition-colors" />
                  <input
                    type="text"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Search comics..."
                    className="w-full pl-9 pr-16 py-2 bg-transparent text-white text-sm placeholder-gray-500 focus:outline-none"
                  />
                  {searchInput && (
                    <button type="button" onClick={clearSearch} className="absolute right-12 p-1 text-gray-500 hover:text-purple-300 transition-colors">
                      <X className="w-3 h-3" />
                    </button>
                  )}
                  <button type="submit" className="absolute right-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-purple-600 to-violet-600 text-white text-xs font-bold uppercase tracking-wide hover:shadow-lg hover:shadow-purple-500/30 transition-all">
                    Go
                  </button>
                </div>
              </form>
            </div>

            {/* Category pills — always visible */}
              <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
                <button
                  onClick={() => handleBackToCategories()}
                  className={`px-4 py-1.5 rounded-full text-xs font-bold tracking-wide transition-all duration-300 whitespace-nowrap ${!activeCategory
                    ? 'bg-gradient-to-r from-purple-600 to-violet-600 text-white shadow-lg shadow-purple-500/30'
                    : 'text-gray-400 border border-purple-500/20 hover:text-white hover:border-purple-500/40 bg-slate-900/40'
                    }`}
                >
                  🔥 All
                </button>
                {CATEGORY_DATA.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryClick(cat.id)}
                    className={`px-4 py-1.5 rounded-full text-xs font-bold tracking-wide transition-all duration-300 whitespace-nowrap ${activeCategory === cat.id
                      ? 'bg-gradient-to-r from-purple-600 to-violet-600 text-white shadow-lg shadow-purple-500/30'
                      : 'text-gray-400 border border-purple-500/20 hover:text-white hover:border-purple-500/40 bg-slate-900/40'
                      }`}
                  >
                    {cat.emoji} {cat.label}
                  </button>
                ))}
              </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 px-4 lg:px-8 py-6 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-16">
                <div className="animate-spin rounded-full h-12 w-12 border-2 border-purple-500 border-t-transparent"></div>
              </div>
            ) : comics.length === 0 ? (
              <div className="text-center py-16 text-gray-500">
                <Sparkles className="w-16 h-16 mx-auto mb-4 text-purple-500/30" />
                <p className="text-lg">No comics found{activeCategory ? ` in ${activeCategory}` : ''}</p>
                <p className="text-sm mt-2">Be the first to create one!</p>
                {user && (
                  <Link
                    to="/create-comic"
                    className="inline-flex items-center mt-4 px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-violet-600 text-white font-bold text-sm hover:shadow-lg hover:shadow-purple-500/30 transition-all"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Create Comic
                  </Link>
                )}
              </div>
            ) : (
              /* ===== COMICS GRID ===== */
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-4">
                {comics.map((comic) => (
                  <button
                    key={comic.id}
                    onClick={() => handleComicClick(comic)}
                    onMouseEnter={() => setSelectedComic(comic)}
                    className={`relative aspect-[2/3] rounded-xl overflow-hidden transition-all duration-300 group ${selectedComic?.id === comic.id
                      ? 'ring-2 ring-purple-500 shadow-[0_0_25px_rgba(168,85,247,0.5)] scale-105 z-10'
                      : 'hover:scale-105 hover:ring-1 hover:ring-purple-400/40'
                      }`}
                  >
                    {comic.episodes?.[0]?.slides?.[0] ? (
                      <img
                        src={comic.episodes[0].slides[0]}
                        alt={comic.title}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    ) : (
                      <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-purple-900 to-slate-800 flex items-center justify-center">
                        <Sparkles className="w-10 h-10 text-purple-500/40" />
                      </div>
                    )}

                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-80 group-hover:opacity-60 transition-opacity"></div>

                    <div className="absolute top-2 right-2 bg-purple-600/80 backdrop-blur-sm px-2 py-0.5 rounded-full text-[9px] font-bold text-white uppercase">
                      {comic.categories?.[0] || 'Comic'}
                    </div>

                    {comic.updated_at && (Date.now() - new Date(comic.updated_at).getTime()) < 7 * 24 * 60 * 60 * 1000 && (
                      <div className="absolute top-1 left-1 z-20" style={{ animation: 'badgeBounce 2s ease-in-out infinite' }}>
                        <div className="relative">
                          <div className="bg-yellow-400 text-black px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wide shadow-lg"
                            style={{
                              border: '2px solid #000',
                              textShadow: '0 0 4px rgba(255,255,0,0.3)',
                              boxShadow: '0 0 12px rgba(250,204,21,0.6), 0 2px 8px rgba(0,0,0,0.4)'
                            }}>
                            ⚡ NEW!
                          </div>
                          <div className="absolute -bottom-1.5 left-3 w-0 h-0"
                            style={{ borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: '6px solid #000' }}></div>
                          <div className="absolute -bottom-1 left-3.5 w-0 h-0"
                            style={{ borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderTop: '5px solid #facc15' }}></div>
                        </div>
                      </div>
                    )}

                    {comic.episodes && comic.episodes.length > 1 && (
                      <div className="absolute top-8 right-2 bg-cyan-500/80 backdrop-blur-sm px-2 py-0.5 rounded-full text-[9px] font-bold text-white">
                        {comic.episodes.length} Eps
                      </div>
                    )}

                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      <p className="text-sm font-bold text-white line-clamp-2 mb-1 drop-shadow-lg">
                        {comic.title}
                      </p>
                      <Link
                        to={`/profile/${comic.author_name}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-[10px] text-purple-300 truncate block hover:text-purple-200 transition-colors"
                      >
                        by {comic.author_name}
                      </Link>
                      <div className="flex items-center gap-3 mt-2 text-[10px] text-gray-400">
                        <span className="flex items-center gap-1">
                          <Heart className="w-3 h-3 text-pink-400" />
                          {comic.likes_count || 0}
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageCircle className="w-3 h-3 text-cyan-400" />
                          {comic.comments_count || 0}
                        </span>
                      </div>
                    </div>

                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="bg-purple-600/90 rounded-full p-4 shadow-xl shadow-purple-500/30 transform scale-75 group-hover:scale-100 transition-transform">
                        <Play className="w-6 h-6 text-white fill-current" />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Lightbox Modal - Comic Details */}
      {isModalOpen && selectedComic && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-md"
            onClick={closeModal}
          ></div>

          <div className="relative max-w-5xl w-full max-h-[90vh] overflow-y-auto rounded-3xl bg-slate-900/95 border border-purple-500/20 shadow-2xl shadow-purple-500/20 animate-fadeIn">
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-slate-800/80 hover:bg-purple-600 text-gray-400 hover:text-white transition-all"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="flex flex-col lg:flex-row">
              <div className="lg:w-[45%] flex-shrink-0 p-6 lg:p-8">
                <div className="relative aspect-[2/3] rounded-2xl overflow-hidden shadow-2xl shadow-purple-500/30 border border-white/10">
                  {selectedComic.episodes?.[0]?.slides?.[0] ? (
                    <img
                      src={selectedComic.episodes[0].slides[0]}
                      alt={selectedComic.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-purple-900 to-slate-900 flex items-center justify-center">
                      <Sparkles className="w-24 h-24 text-purple-500/30" />
                    </div>
                  )}
                  <div className="absolute top-4 right-4 bg-gradient-to-br from-purple-600 to-violet-600 px-4 py-1.5 rounded-full text-xs font-bold text-white uppercase tracking-wider shadow-lg shadow-purple-500/40">
                    {selectedComic.categories?.[0] || 'Comic'}
                  </div>
                </div>
              </div>

              <div className="lg:w-[55%] p-6 lg:p-8 lg:pl-0 flex flex-col justify-center">
                <p className="text-sm text-purple-400 font-medium mb-3 uppercase tracking-widest">
                  Created by{' '}
                  <Link
                    to={`/profile/${selectedComic.author_name}`}
                    className="text-white hover:text-purple-300 underline decoration-purple-500/50 hover:decoration-purple-400 transition-colors"
                    onClick={closeModal}
                  >
                    {selectedComic.author_name}
                  </Link>
                </p>

                <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white uppercase tracking-tight leading-none mb-6">
                  {selectedComic.title}
                </h2>

                <p className="text-gray-300 text-base md:text-lg leading-relaxed mb-8 line-clamp-5">
                  {selectedComic.description || 'An amazing comic waiting to be discovered. Dive into this creative story and experience something unique.'}
                </p>

                <div className="flex items-center gap-6 mb-8">
                  <div className="flex items-center gap-2 text-pink-400">
                    <Heart className="w-5 h-5" />
                    <span className="font-bold text-lg">{selectedComic.likes_count || 0}</span>
                    <span className="text-gray-500 text-sm">likes</span>
                  </div>
                  <div className="flex items-center gap-2 text-cyan-400">
                    <MessageCircle className="w-5 h-5" />
                    <span className="font-bold text-lg">{selectedComic.comments_count || 0}</span>
                    <span className="text-gray-500 text-sm">comments</span>
                  </div>
                </div>

                <Link
                  to={`/streamlined-comic/${selectedComic.id}`}
                  className="inline-flex items-center justify-center w-full lg:w-auto px-10 py-4 rounded-2xl bg-gradient-to-r from-purple-600 to-violet-600 text-white font-bold text-lg uppercase tracking-wider shadow-xl shadow-purple-500/30 hover:shadow-purple-500/50 hover:scale-105 transition-all duration-300 group"
                >
                  <Play className="w-6 h-6 mr-3 fill-current" />
                  Read Now
                  <ChevronDown className="w-5 h-5 ml-2 -rotate-90 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default Home;
